import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  TrendingUp as LevelIcon,
  Category as TypeIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { coachingProgramAPI } from '../../services/api';
import { CoachingProgram } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';
import { useRequiredMemberContext } from '../../hooks/useMemberContext';

const CoachingProgramsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, memberProfile, isReady } = useRequiredMemberContext();

  const [programs, setPrograms] = useState<CoachingProgram[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<CoachingProgram[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [selectedProgram, setSelectedProgram] = useState<CoachingProgram | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Filter states
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [coachFilter, setCoachFilter] = useState<string>('');

  // Available filter options
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [availableLevels, setAvailableLevels] = useState<string[]>([]);
  const [availableCoaches, setAvailableCoaches] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (isReady && memberProfile?.id) {
      fetchPrograms();
    }
  }, [isReady, memberProfile?.id]);

  useEffect(() => {
    applyFilters();
  }, [programs, searchFilter, typeFilter, levelFilter, coachFilter]);

  const fetchPrograms = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all active programs (both SCHEDULED and ACTIVE status)
      const response = await coachingProgramAPI.getCoachingPrograms({});
      
      const programsList = response.data;
      // Filter to only show scheduled programs that members can enroll in
      const availablePrograms = programsList.filter((p: CoachingProgram) => 
        p.active && p.status === 'SCHEDULED'
      );
      setPrograms(availablePrograms);

      // Extract unique values for filters
      const types = [...new Set(availablePrograms.map((p: CoachingProgram) => p.type))].filter(Boolean);
      const levels = [...new Set(availablePrograms.map((p: CoachingProgram) => p.level))].filter(Boolean);
      const coaches = [...new Set(availablePrograms.map((p: CoachingProgram) => ({
        id: p.coachId || '',
        name: p.coachName
      })).filter((c: any) => c.name))];

      setAvailableTypes(types as string[]);
      setAvailableLevels(levels as string[]);
      setAvailableCoaches(coaches as any[]);
    } catch (error: any) {
      console.error('Error fetching coaching programs:', error);
      setError(error.response?.data?.message || 'Failed to load coaching programs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...programs];

    // Text search filter
    if (searchFilter) {
      const searchLower = searchFilter.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchLower) ||
        p.type.toLowerCase().includes(searchLower) ||
        p.level.toLowerCase().includes(searchLower) ||
        p.coachName.toLowerCase().includes(searchLower) ||
        (p.description && p.description.toLowerCase().includes(searchLower))
      );
    }

    if (typeFilter) {
      filtered = filtered.filter(p => p.type === typeFilter);
    }

    if (levelFilter) {
      filtered = filtered.filter(p => p.level === levelFilter);
    }

    if (coachFilter) {
      filtered = filtered.filter(p => p.coachId === coachFilter || p.coachName === coachFilter);
    }

    setFilteredPrograms(filtered);
  };

  const handleClearFilters = () => {
    setSearchFilter('');
    setTypeFilter('');
    setLevelFilter('');
    setCoachFilter('');
  };

  const handleBookClick = (program: CoachingProgram) => {
    setSelectedProgram(program);
    setOpenConfirmDialog(true);
  };

  const handleConfirmBooking = async () => {
    if (!selectedProgram || !memberProfile) return;

    setBookingLoading(true);
    setError(null);

    try {
      const enrollmentData = {
        memberNotes: '',
        enrollmentType: 'FULL_PROGRAM'
      };

      const response = await coachingProgramAPI.enrollInCoachingProgram(
        selectedProgram.id,
        enrollmentData
      );

      setBookingSuccess(true);
      setSuccessMessage('Successfully enrolled in coaching program!');
      setOpenConfirmDialog(false);

      // Refresh programs to update enrollment counts
      setTimeout(() => {
        fetchPrograms();
        setBookingSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      console.error('Error enrolling in coaching program:', error);
      setError(error.response?.data?.message || 'Failed to enroll in coaching program');
      setOpenConfirmDialog(false);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelDialog = () => {
    setOpenConfirmDialog(false);
    setSelectedProgram(null);
  };

  const getProgramImageUrl = (program: CoachingProgram) => {
    if (program.image) {
      // Handle full URLs
      if (program.image.startsWith('http://') || program.image.startsWith('https://')) {
        return program.image;
      }
      // Handle relative paths
      return `${import.meta.env.VITE_API_URL || 'http://localhost:5000'}${program.image}`;
    }
    // Default placeholder image
    return '/placeholder-program.jpg';
  };

  const calculateProgramCost = (program: CoachingProgram) => {
    // Check if program has totalCost field (might be stored as 'cost' in some cases)
    const totalCost = (program as any).totalCost || (program as any).cost || 0;
    return Number(totalCost);
  };

  const calculateDropInCost = (program: CoachingProgram) => {
    const dropInCost = (program as any).dropInRate || (program as any).dropInCost || 0;
    return Number(dropInCost);
  };

  const formatRecurrenceSchedule = (program: CoachingProgram) => {
    if (!program.recurringPattern) return null;

    const { frequency, daysOfWeek, startTime, endTime, duration } = program.recurringPattern;
    
    // Map day numbers to names
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const selectedDays = daysOfWeek.map(day => dayNames[day]).join(', ');

    // Format frequency
    let frequencyText = '';
    if (frequency === 'weekly') {
      frequencyText = 'Every week';
    } else if (frequency === 'biweekly') {
      frequencyText = 'Every 2 weeks';
    } else if (frequency === 'monthly') {
      frequencyText = 'Monthly';
    }

    // Format time with duration
    const formattedStartTime = formatTimeString(startTime);
    const durationText = duration ? ` (${duration} min)` : '';

    return {
      schedule: `${frequencyText} on ${selectedDays}`,
      time: `${formattedStartTime}${durationText}`
    };
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Coaching Programs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Browse and enroll in available coaching programs
          </Typography>
        </Box>

        {/* Success Message */}
        {bookingSuccess && (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />}
            sx={{ mb: 3 }}
          >
            {successMessage}
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search programs by name, type, level, coach, or description..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {availableTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Level</InputLabel>
                <Select
                  value={levelFilter}
                  label="Level"
                  onChange={(e) => setLevelFilter(e.target.value)}
                >
                  <MenuItem value="">All Levels</MenuItem>
                  {availableLevels.map(level => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Coach</InputLabel>
                <Select
                  value={coachFilter}
                  label="Coach"
                  onChange={(e) => setCoachFilter(e.target.value)}
                >
                  <MenuItem value="">All Coaches</MenuItem>
                  {availableCoaches.map(coach => (
                    <MenuItem key={coach.id || coach.name} value={coach.name}>
                      {coach.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Programs List */}
        {filteredPrograms.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No coaching programs found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {programs.length === 0 
                ? 'There are currently no active coaching programs available.'
                : 'Try adjusting your filters to see more programs.'}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredPrograms.map((program) => {
              const totalCost = calculateProgramCost(program);
              const dropInCost = calculateDropInCost(program);
              const enrolledCount = (program as any).enrolledCount || (program as any).currentEnrollments || 0;
              const availableSpots = (program as any).availableSpots !== undefined 
                ? (program as any).availableSpots 
                : program.capacity - enrolledCount;
              const isFull = availableSpots <= 0;

              return (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={program.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {program.image && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={getProgramImageUrl(program)}
                        alt={program.name}
                        sx={{ objectFit: 'cover' }}
                        onError={(e: any) => {
                          e.target.src = '/placeholder-program.jpg';
                        }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {program.name}
                      </Typography>
                      
                      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Chip label={program.type} size="small" color="primary" variant="outlined" />
                        <Chip label={program.level} size="small" color="secondary" variant="outlined" />
                        <Chip 
                          label={isFull ? 'Full' : 'Available'} 
                          size="small" 
                          color={isFull ? 'error' : 'success'} 
                        />
                      </Stack>

                      {program.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {program.description.length > 120 
                            ? `${program.description.substring(0, 120)}...` 
                            : program.description}
                        </Typography>
                      )}

                      <List dense disablePadding>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <PersonIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                          <ListItemText 
                            primary={program.coachName || 'TBD'}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <CalendarIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                          <ListItemText 
                            primary={`${formatDate(program.startDate)} - ${formatDate(program.endDate)}`}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        {formatRecurrenceSchedule(program) && (
                          <>
                            <ListItem sx={{ px: 0, py: 0.5 }}>
                              <CalendarIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                              <ListItemText 
                                primary={formatRecurrenceSchedule(program)!.schedule}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                            <ListItem sx={{ px: 0, py: 0.5 }}>
                              <ScheduleIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                              <ListItemText 
                                primary={formatRecurrenceSchedule(program)!.time}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          </>
                        )}
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <PersonIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                          <ListItemText 
                            primary={`${enrolledCount} / ${program.capacity} enrolled`}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        {totalCost > 0 && (
                          <ListItem sx={{ px: 0, py: 0.5 }}>
                            <MoneyIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                            <ListItemText 
                              primary={`${totalCost.toFixed(2)}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                            />
                          </ListItem>
                        )}
                        {/* {dropInCost > 0 && (
                          <ListItem sx={{ px: 0, py: 0.5 }}>
                            <MoneyIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                            <ListItemText 
                              primary={`$${dropInCost.toFixed(2)} per session`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        )} */}
                      </List>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleBookClick(program)}
                        disabled={isFull || bookingLoading}
                      >
                        {isFull ? 'Program Full' : 'Enroll Now'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Booking Confirmation Dialog */}
        <Dialog
          open={openConfirmDialog}
          onClose={handleCancelDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Confirm Enrollment
          </DialogTitle>
          <DialogContent>
            {selectedProgram && (
              <>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to enroll in this coaching program?
                </Typography>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Program:</strong> {selectedProgram.name}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Type:</strong> {selectedProgram.type}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Level:</strong> {selectedProgram.level}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Coach:</strong> {selectedProgram.coachName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Start Date:</strong> {formatDate(selectedProgram.startDate)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>End Date:</strong> {formatDate(selectedProgram.endDate)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Member:</strong> {memberProfile?.firstName} {memberProfile?.lastName}
                  </Typography>
                  {calculateProgramCost(selectedProgram) > 0 && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
                      <strong>Total Cost:</strong> ${calculateProgramCost(selectedProgram).toFixed(2)}
                    </Typography>
                  )}
                </Box>
                {calculateProgramCost(selectedProgram) > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    A charge of ${calculateProgramCost(selectedProgram).toFixed(2)} will be applied to your account.
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDialog} disabled={bookingLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBooking}
              variant="contained"
              disabled={bookingLoading}
              startIcon={bookingLoading ? <CircularProgress size={20} /> : null}
            >
              {bookingLoading ? 'Enrolling...' : 'Confirm Enrollment'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CoachingProgramsPage;
